import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';

import { useAuth } from '../../contexts/AuthContext';
import {
  agentConfigService,
  GlobalCompanyContext as GlobalCompanyContextRecord,
} from '../../services/agentConfigService';

const GlobalCompanyContext: React.FC = () => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [record, setRecord] = useState<GlobalCompanyContextRecord | null>(null);
  const [content, setContent] = useState('');
  const [isActive, setIsActive] = useState(true);

  const [initialContent, setInitialContent] = useState('');
  const [initialIsActive, setInitialIsActive] = useState(true);

  const dirty = useMemo(() => {
    return content !== initialContent || isActive !== initialIsActive;
  }, [content, initialContent, isActive, initialIsActive]);

  const loadContext = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await agentConfigService.getGlobalCompanyContext();
      setRecord(data);
      setContent(data?.content || '');
      setIsActive(data?.is_active ?? true);
      setInitialContent(data?.content || '');
      setInitialIsActive(data?.is_active ?? true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Error al cargar el contexto global de empresa');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContext();
  }, []);

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSave = async () => {
    const trimmed = content.trim();
    if (!trimmed) {
      showSnackbar('El contenido no puede estar vacío', 'error');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const saved = await agentConfigService.upsertGlobalCompanyContext({
        content: trimmed,
        is_active: isActive,
        updated_by: user?.username,
      });
      setRecord(saved);
      setContent(saved.content);
      setIsActive(saved.is_active);
      setInitialContent(saved.content);
      setInitialIsActive(saved.is_active);
      showSnackbar('Contexto global guardado. Sincronización al vector store iniciada.', 'success');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Error al guardar el contexto global de empresa');
      showSnackbar('No fue posible guardar el contexto global', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setContent(initialContent);
    setIsActive(initialIsActive);
    setError(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={220}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Contexto Global de Empresa
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Este texto se indexa en el vector store y permite responder preguntas generales sobre la empresa.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          multiline
          minRows={12}
          label="Contexto Global"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          helperText="Describe trayectoria, valores, propuesta de valor y capacidades generales de la empresa."
        />

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
            label="Contexto global activo"
          />
        </Box>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            Última actualización:{' '}
            {record?.updated_at ? new Date(record.updated_at).toLocaleString() : 'sin registro'}
            {record?.updated_by ? ` por ${record.updated_by}` : ''}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={handleReset} disabled={!dirty || saving}>
              Restablecer
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={!dirty || saving}
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GlobalCompanyContext;
